import type { Facets, FacetType } from '@seek/chalice-types';
import {
  Button,
  Inline,
  Stack,
  Text,
  Box,
  IconAI,
  TextLinkButton,
  useResponsiveValue,
  useToast,
  Heading,
} from 'braid-design-system';
import { useState, useMemo } from 'react';

import AnimatedHeight from 'src/components/AnimatedHeight/AnimatedHeight';
import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useHotjarEvent } from 'src/hooks/useHotjarEvent';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useDispatch, useSelector } from 'src/store/react';
import { updateKeywordsField } from 'src/store/search';
import { useRunSearch } from 'src/store/search/useRunSearch';
import { selectSearchQuery, selectSolMetadata } from 'src/store/selectors';
import { filterFacetNudgePillsParams } from 'src/utils/filterFacetNudgePillsParams';
import { getFacetLabelText } from 'src/utils/getFacetLabelText';

import { NudgePill } from './NudgePill';
import { MAX_VISIBLE_PILLS_COLLAPSED, MAX_DISPLAY_PILLS } from './constants';

import * as styles from './NudgeIntent.css';

interface NudgeIntentProps {
  facetTypesData: FacetType[];
  title: string;
  description: string;
  displayToastMessage: string;
  isMultiSelect: boolean;
  facetKey: keyof Facets;
}

const getTypeFromFacetKey = (key: string) => {
  switch (key) {
    case 'workArrangement':
      return 'WORK_ARRANGEMENT';
    case 'workType':
      return 'WORK_TYPES';
    case 'salaryMin':
      return 'SALARY_MIN';
    default:
      return 'OTHER';
  }
};

export const NudgeIntent = ({
  facetTypesData,
  title,
  description,
  displayToastMessage,
  isMultiSelect,
  facetKey,
}: NudgeIntentProps) => {
  const analyticsFacade = useAnalyticsFacade();
  const solMetadata = useSelector(selectSolMetadata);

  const dispatch = useDispatch();
  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) || false;

  const showToast = useToast();
  const query = getRefineSearchQuery(useSelector(selectSearchQuery));
  const runSearch = useRunSearch();

  const [selectedNudgePills, setSelectedNudgePills] = useState<
    Map<string, { id: string; label: string; index: number }>
  >(new Map());
  const [expanded, setExpanded] = useState(false);
  const isNudgePillSelected = selectedNudgePills.size > 0;
  // filter out salaryMin with label '0' first as we dont want to show it as a nudge pill
  const filterSalaryMin = facetTypesData.filter(({ label }) => label !== '0');
  const maximumNudgePillsToShow = filterSalaryMin.slice(0, MAX_DISPLAY_PILLS);
  const expandMoreNudgePills =
    maximumNudgePillsToShow.length > MAX_VISIBLE_PILLS_COLLAPSED;

  const numberOfHiddenPills =
    maximumNudgePillsToShow.length - MAX_VISIBLE_PILLS_COLLAPSED;

  const nudgePillsWithActiveState = useMemo(
    () =>
      maximumNudgePillsToShow
        // Filter out pills with empty labels (string or object)
        .filter((pill) => getFacetLabelText(pill.label) !== '')
        .map((pill, index) => ({
          ...pill,
          isActive: selectedNudgePills.has(pill.id),
          index,
        })),
    [maximumNudgePillsToShow, selectedNudgePills],
  );

  const handleNudgePillClick = (pillId: string) => {
    setSelectedNudgePills((previousSelection) => {
      const wasSelected = previousSelection.has(pillId);

      if (isMultiSelect) {
        const newMap = new Map(previousSelection);
        if (wasSelected) {
          newMap.delete(pillId);
        } else {
          const pill = nudgePillsWithActiveState.find((p) => p.id === pillId);
          if (pill) {
            newMap.set(pillId, {
              id: pill.id,
              label: getFacetLabelText(pill.label, facetKey),
              index: pill.index,
            });
          }
        }
        return newMap;
      }
      // Single-select
      if (wasSelected) {
        return new Map();
      }
      const pill = nudgePillsWithActiveState.find((p) => p.id === pillId);
      if (pill) {
        return new Map([
          [
            pillId,
            {
              id: pill.id,
              label: getFacetLabelText(pill.label, facetKey),
              index: pill.index,
            },
          ],
        ]);
      }
      return new Map();
    });
  };

  const nudgePills = nudgePillsWithActiveState
    ?.filter(({ label }) => label !== '0')
    ?.map(({ id, label, count }) => (
      <NudgePill
        id={id}
        key={id}
        label={getFacetLabelText(label, facetKey)}
        count={count}
        isActive={selectedNudgePills.has(id)}
        isMobile={isMobile}
        onClick={handleNudgePillClick}
        facetKey={facetKey}
      />
    ));

  const selectedJobsCount = useMemo(
    () =>
      Array.from(selectedNudgePills.keys()).reduce(
        (sum, id) =>
          sum +
          (maximumNudgePillsToShow.find((pill) => pill.id === id)?.count || 0),
        0,
      ),
    [selectedNudgePills, maximumNudgePillsToShow],
  );

  const handleSelectedJobsCountButtonClick = () => {
    if (facetKey === 'distinctTitle') {
      // single-select for distinctTitle
      // convert the map keys to an array to get value
      const selectedId = Array.from(selectedNudgePills.keys())[0];
      const pillSelected = maximumNudgePillsToShow.find(
        (p) => p.id === selectedId,
      );
      const facetLabelText = getFacetLabelText(pillSelected?.label);
      dispatch(updateKeywordsField(facetLabelText));
      dispatch(
        runSearch({
          pathname: '/jobs',
        }),
      );

      analyticsFacade.refinementSuggestionClicked({
        solMetadata,
        refinedKeyword: Array.from(selectedNudgePills.values()).map(
          ({ label, index }) => ({
            isActive: true,
            label,
            position: index,
            type: 'DISTINCT_TITLE',
          }),
        ),
      });
    } else {
      // Other facetKeys - workType, workArrangement, salaryMin
      // Convert Map keys to Set for filterFacetNudgePillsParams
      const selectedIdsSet = new Set(selectedNudgePills.keys());
      dispatch(
        runSearch({
          pathname: '/jobs',
          overrideQuery: {
            ...query,
            ...filterFacetNudgePillsParams(facetKey, selectedIdsSet),
          },
        }),
      );

      analyticsFacade.refinementSuggestionClicked({
        solMetadata,
        refinedKeyword: Array.from(selectedNudgePills.values()).map(
          ({ label, index }) => ({
            isActive: true,
            label,
            position: index,
            type: getTypeFromFacetKey(facetKey),
          }),
        ),
      });
    }
    showToast({
      message: displayToastMessage,
      tone: 'positive',
    });
  };

  const displayAllNudgePills = nudgePills.slice(0, nudgePills.length);

  const displayMinNudgePills = nudgePills.slice(0, MAX_VISIBLE_PILLS_COLLAPSED);

  useHotjarEvent('srp_in_flow_nudging_experiment');

  return (
    <Box
      paddingTop="medium"
      paddingX="medium"
      boxShadow="borderPromoteLight"
      borderRadius="large"
      data-automation={`nudge-intent-${facetKey}`}
    >
      <Stack space="medium">
        <Stack space="small">
          <Inline space="none" alignY="center">
            <Heading level="4" weight="regular">
              <IconAI tone="promote" />
            </Heading>
            <Text
              weight="strong"
              tone="promote"
              size={isMobile ? 'small' : 'standard'}
            >
              {title}
            </Text>
          </Inline>
          <Text size={isMobile ? 'small' : 'standard'}>{description}</Text>
        </Stack>
        <AnimatedHeight changeKeys={[expanded]}>
          <Inline
            space={{ mobile: 'xxsmall', tablet: 'xsmall' }}
            alignY="center"
          >
            {expanded ? displayAllNudgePills : displayMinNudgePills}

            {expandMoreNudgePills && !expanded && (
              <Box
                data-automation="expandMoreNudgePills"
                component="button"
                onClick={() => setExpanded(true)}
              >
                <Text size="small">
                  <TextLinkButton weight="weak">
                    +{numberOfHiddenPills} more
                  </TextLinkButton>
                </Text>
              </Box>
            )}

            {expandMoreNudgePills && expanded && (
              <Box
                data-automation="expandMoreNudgePills"
                component="button"
                onClick={() => setExpanded(false)}
              >
                <Text size="small">
                  <TextLinkButton weight="weak">Show less</TextLinkButton>
                </Text>
              </Box>
            )}
          </Inline>
        </AnimatedHeight>
        <Box
          className={{
            [styles.buttonContainer]: true,
            [styles.buttonContainerVisible]: isNudgePillSelected,
          }}
        >
          <Button
            data-automation="selectedJobsCountButton"
            onClick={handleSelectedJobsCountButtonClick}
            variant="solid"
            tone="brandAccent"
          >
            {isNudgePillSelected && facetKey !== 'distinctTitle'
              ? `SEEK ${selectedJobsCount.toLocaleString()} jobs`
              : `SEEK`}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
